import axios from 'axios';
import {
    KnowledgeBase,
    CreateKnowledgeBaseRequest,
    UpdateKnowledgeBaseRequest,
    KnowledgeBaseFilter,
    KnowledgeBaseStats,
    PaginatedResponse
} from '@shared/types';

// API基础配置 - 强制使用生产环境URL
const API_BASE_URL = 'https://ai-learning-backend.risingstarintl.biz';

// 创建axios实例
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 增加到30秒
    headers: {
        'Content-Type': 'application/json',
    },
});

// 请求拦截器
apiClient.interceptors.request.use(
    (config) => {
        console.log(`API请求: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => {
        console.error('API请求错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
apiClient.interceptors.response.use(
    (response) => {
        console.log(`API响应: ${response.status} ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error('API响应错误:', error.response?.data || error.message);
        return Promise.reject(error);
    }
);

// 知识库API服务
export const knowledgeBaseAPI = {
    // 获取知识库列表
    async getKnowledgeBases(filter?: KnowledgeBaseFilter): Promise<PaginatedResponse<KnowledgeBase>> {
        const params = new URLSearchParams();
        if (filter) {
            Object.entries(filter).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    params.append(key, value.toString());
                }
            });
        }

        const response = await apiClient.get(`/api/knowledge-bases?${params.toString()}`);
        return response.data;
    },

    // 获取知识库详情
    async getKnowledgeBaseById(id: string): Promise<KnowledgeBase> {
        const response = await apiClient.get(`/api/knowledge-bases/${id}`);
        return response.data.data;
    },

    // 创建知识库
    async createKnowledgeBase(data: CreateKnowledgeBaseRequest): Promise<{
        success: boolean;
        data?: KnowledgeBase;
        error?: string;
        message?: string;
    }> {
        const response = await apiClient.post('/api/knowledge-bases', data);
        return response.data;
    },

    // 更新知识库
    async updateKnowledgeBase(id: string, data: UpdateKnowledgeBaseRequest): Promise<{
        success: boolean;
        data?: KnowledgeBase;
        error?: string;
        message?: string;
    }> {
        const response = await apiClient.put(`/api/knowledge-bases/${id}`, data);
        return response.data;
    },

    // 删除知识库
    async deleteKnowledgeBase(id: string): Promise<{
        success: boolean;
        error?: string;
        message?: string;
    }> {
        const response = await apiClient.delete(`/api/knowledge-bases/${id}`);
        return response.data;
    },

    // 获取知识库统计信息
    async getKnowledgeBaseStats(): Promise<KnowledgeBaseStats> {
        const response = await apiClient.get('/api/knowledge-bases/stats');
        return response.data.data;
    }
};

// 健康检查
export const healthAPI = {
    async check(): Promise<{ status: string; timestamp: string }> {
        const response = await apiClient.get('/health');
        return response.data;
    }
};

// PDF文档上传和管理API
export const documentAPI = {
    // 上传PDF文档
    async uploadDocument(formData: FormData): Promise<{
        success: boolean;
        document_id?: string;
        message?: string;
        file_info?: {
            name: string;
            size: number;
            type: string;
        };
        content_preview?: string;
        error?: string;
    }> {
        const response = await apiClient.post('/api/documents/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: 30000, // 30秒超时
        });
        return response.data;
    },

    // 获取文档状态
    async getDocumentStatus(documentId: string): Promise<{
        success: boolean;
        document?: {
            id: string;
            status: string;
            processing_progress: number;
            created_at: string;
            updated_at: string;
        };
        error?: string;
    }> {
        const response = await apiClient.get(`/api/documents/status/${documentId}`);
        return response.data;
    },

    // 获取知识库的文档列表
    async getKnowledgeBaseDocuments(knowledgeBaseId: string): Promise<{
        success: boolean;
        documents?: Array<{
            id: string;
            knowledge_base_id: string;
            filename: string;
            file_size: number;
            file_type: string;
            status: string;
            processing_progress: number;
            created_at: string;
        }>;
        total?: number;
        error?: string;
    }> {
        const response = await apiClient.get(`/api/documents/knowledge-base/${knowledgeBaseId}`);
        return response.data;
    },

    // 删除文档
    async deleteDocument(documentId: string): Promise<{
        success: boolean;
        message?: string;
        error?: string;
    }> {
        const response = await apiClient.delete(`/api/documents/${documentId}`);
        return response.data;
    },

    // 获取文档查看URL
    async getDocumentViewUrl(documentId: string): Promise<{
        success: boolean;
        viewUrl?: string;
        downloadUrl?: string;
        error?: string;
    }> {
        const response = await apiClient.get(`/api/documents/${documentId}/view-url`);
        return response.data;
    },

    // 获取文档内容预览
    async getDocumentPreview(documentId: string): Promise<{
        success: boolean;
        content?: string;
        pages?: number;
        error?: string;
    }> {
        const response = await apiClient.get(`/api/documents/${documentId}/preview`);
        return response.data;
    }
};

// 导出API客户端实例（用于其他服务）
export { apiClient };
